// src/MapWithSearch.tsx
import React, { useState, useRef, useCallback, useEffect } from "react";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import {
  getPlaceAutocomplete,
  getPlaceDetails,
  getPlaceDetailsById,
} from "./services/apis";

const libraries: "places"[] = ["places"];
const AUTOCOMPLETE_DEBOUNCE_MS = 350;
const AUTOCOMPLETE_MIN_CHARS = 3;
const MIN_DRAG_DISTANCE_METERS = 75;
const MIN_REVERSE_LOOKUP_INTERVAL_MS = 2000;

interface OpeningHoursPeriod {
  open: { day: number; time: string };
  close?: { day: number; time: string };
}

interface PlaceOpeningHours {
  weekday_text?: string[];
  periods?: OpeningHoursPeriod[];
}

interface PlaceDetails {
  openingHours?: PlaceOpeningHours | null;
  placeName?: string | null;
  phoneNumber?: string | null;
  formattedAddress?: string | null;
  lat?: number | null;
  lng?: number | null;
}

interface PlaceSuggestion {
  placeId: string;
  description: string;
  mainText?: string;
  secondaryText?: string;
}

interface MapWithSearchProps {
  onLocationChange?: (data: {
    address: string;
    lat: number;
    lng: number;
    flatBlock?: string;
    area?: string;
    fullAddress?: string;
    city?: string;
    state?: string;
    country?: string;
    openingHours?: PlaceOpeningHours | null;
    placeName?: string | null;
    phoneNumber?: string | null;
  }) => void;
  editAddress?: {
    address: string;
    lat: number;
    lng: number;
  } | null;
}

const MapWithSearch: React.FC<MapWithSearchProps> = ({
  onLocationChange,
  editAddress,
}) => {
  const [center, setCenter] = useState(
    editAddress
      ? { lat: Number(editAddress.lat), lng: Number(editAddress.lng) }
      : { lat: 37.7749, lng: -122.4194 },
  );
  const [searchValue, setSearchValue] = useState(
    editAddress ? editAddress.address : "",
  );
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const mapRef = useRef<google.maps.Map | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSessionTokenRef = useRef<string>("");
  const lastAutocompleteQueryRef = useRef<string>("");
  const lastReverseLookupRef = useRef<{
    lat: number;
    lng: number;
    at: number;
  } | null>(null);
  const api_key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const predefinedLocations = [
    { name: "San Francisco, CA", lat: 37.7749, lng: -122.4194 },
    { name: "New York, NY", lat: 40.7128, lng: -74.006 },
    { name: "Los Angeles, CA", lat: 34.0522, lng: -118.2437 },
  ];

  const handleLoadMap = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const generateSessionToken = () => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const ensureSearchSessionToken = () => {
    if (!searchSessionTokenRef.current) {
      searchSessionTokenRef.current = generateSessionToken();
    }
    return searchSessionTokenRef.current;
  };

  const clearSearchSessionToken = () => {
    searchSessionTokenRef.current = "";
    lastAutocompleteQueryRef.current = "";
  };

  const distanceInMeters = (
    fromLat: number,
    fromLng: number,
    toLat: number,
    toLng: number,
  ) => {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const earthRadius = 6371000;
    const deltaLat = toRad(toLat - fromLat);
    const deltaLng = toRad(toLng - fromLng);
    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(toRad(fromLat)) *
        Math.cos(toRad(toLat)) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadius * c;
  };

  // Prevent infinite loop - run initialization only once
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (editAddress) {
      setCenter({ lat: Number(editAddress.lat), lng: Number(editAddress.lng) });
      setSearchValue(editAddress.address);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        setCenter({ lat, lng });
        fetchPlaceDetails(lat, lng);
      },
      () => {
        const fb = predefinedLocations[0];
        setCenter({ lat: fb.lat, lng: fb.lng });
        fetchPlaceDetails(fb.lat, fb.lng);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const parseFormattedAddress = (formattedAddress: string) => {
    const parts = formattedAddress.split(",").map((part) => part.trim());

    let flatBlock = "";
    let area = "";
    let city = "";
    let state = "";
    let country = "";

    if (parts.length >= 1) {
      area = parts[0];
      flatBlock = parts[0];
    }

    if (parts.length >= 2) {
      city = parts[1];
    }

    if (parts.length >= 3) {
      const stateZipPart = parts[2];
      const stateMatch = stateZipPart.match(/^([A-Za-z\s]+)/);
      if (stateMatch) {
        state = stateMatch[1].trim();
      }
    }

    if (parts.length >= 4) {
      country = parts[parts.length - 1];
    } else if (parts.length === 3) {
      const lastPart = parts[2];
      if (!/\d{5}/.test(lastPart) && lastPart.length > 2) {
        country = lastPart;
      }
    }

    return { flatBlock, area, city, state, country };
  };

  const emitLocation = (
    lat: number,
    lng: number,
    formattedAddress: string,
    placeDetails: PlaceDetails,
  ) => {
    const { flatBlock, area, city, state, country } =
      parseFormattedAddress(formattedAddress);

    if (onLocationChange) {
      onLocationChange({
        address: formattedAddress,
        lat,
        lng,
        flatBlock,
        area,
        fullAddress: formattedAddress,
        city,
        state,
        country,
        openingHours: placeDetails.openingHours,
        placeName: placeDetails.placeName,
        phoneNumber: placeDetails.phoneNumber,
      });
    }
  };

  // Fetch place details (opening hours, name, phone, formatted address) from backend API
  const fetchPlaceDetailsFromBackend = async (
    lat: number,
    lng: number,
  ): Promise<PlaceDetails> => {
    try {
      const response = await getPlaceDetails(lat, lng);
      const data = response.data;

      if (data.success) {
        return {
          openingHours: data.data || null,
          placeName: data.placeName || null,
          phoneNumber: data.phoneNumber || null,
          formattedAddress: data.formattedAddress || data.address || null,
        };
      }

      return {
        openingHours: null,
        placeName: null,
        phoneNumber: null,
        formattedAddress: null,
      };
    } catch (error) {
      console.error("Error fetching place details from backend:", error);
      return {
        openingHours: null,
        placeName: null,
        phoneNumber: null,
        formattedAddress: null,
      };
    }
  };

  const fetchPlaceDetailsByIdFromBackend = async (
    placeId: string,
    sessionToken?: string,
  ): Promise<PlaceDetails> => {
    try {
      const response = await getPlaceDetailsById(placeId, sessionToken);
      const data = response.data;

      if (data.success) {
        return {
          openingHours: data.data || null,
          placeName: data.placeName || null,
          phoneNumber: data.phoneNumber || null,
          formattedAddress: data.formattedAddress || data.address || null,
          lat: typeof data.lat === "number" ? data.lat : null,
          lng: typeof data.lng === "number" ? data.lng : null,
        };
      }

      return {
        openingHours: null,
        placeName: null,
        phoneNumber: null,
        formattedAddress: null,
        lat: null,
        lng: null,
      };
    } catch (error) {
      console.error("Error fetching place details by id from backend:", error);
      return {
        openingHours: null,
        placeName: null,
        phoneNumber: null,
        formattedAddress: null,
        lat: null,
        lng: null,
      };
    }
  };

  const fetchPlaceDetails = async (lat: number, lng: number) => {
    try {
      const placeDetails = await fetchPlaceDetailsFromBackend(lat, lng);
      const formattedAddress = placeDetails.formattedAddress || "";
      if (!formattedAddress) {
        return;
      }

      lastReverseLookupRef.current = {
        lat,
        lng,
        at: Date.now(),
      };
      setSearchValue(formattedAddress);
      emitLocation(lat, lng, formattedAddress, placeDetails);
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  const fetchSuggestions = async (input: string) => {
    try {
      const normalizedInput = input.trim();
      if (normalizedInput.length < AUTOCOMPLETE_MIN_CHARS) {
        setSuggestions([]);
        lastAutocompleteQueryRef.current = "";
        return;
      }

      const normalizedCacheKey = normalizedInput.toLowerCase();
      if (normalizedCacheKey === lastAutocompleteQueryRef.current) {
        return;
      }
      lastAutocompleteQueryRef.current = normalizedCacheKey;

      const response = await getPlaceAutocomplete(
        normalizedInput,
        center.lat,
        center.lng,
        ensureSearchSessionToken(),
      );
      const data = response.data;
      if (data.success) {
        setSuggestions(data.data || []);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error("Error fetching autocomplete suggestions:", error);
      setSuggestions([]);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setShowSuggestions(true);
    ensureSearchSessionToken();

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, AUTOCOMPLETE_DEBOUNCE_MS);
  };

  const handleSuggestionSelect = async (suggestion: PlaceSuggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);

    const sessionToken = ensureSearchSessionToken();
    const placeDetails = await fetchPlaceDetailsByIdFromBackend(
      suggestion.placeId,
      sessionToken,
    );
    clearSearchSessionToken();

    const lat = placeDetails.lat;
    const lng = placeDetails.lng;
    const formattedAddress =
      placeDetails.formattedAddress || suggestion.description || "";

    if (
      typeof lat !== "number" ||
      typeof lng !== "number" ||
      !formattedAddress
    ) {
      return;
    }

    lastReverseLookupRef.current = {
      lat,
      lng,
      at: Date.now(),
    };
    setCenter({ lat, lng });
    setSearchValue(formattedAddress);
    emitLocation(lat, lng, formattedAddress, placeDetails);

    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(13);
    }
  };

  const handleSearchFocus = () => {
    setShowSuggestions(true);
    ensureSearchSessionToken();
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      clearSearchSessionToken();
    }, 150);
  };

  const handleDragEnd = useCallback(() => {
    if (mapRef.current) {
      const newCenter = mapRef.current.getCenter();
      if (newCenter) {
        const lat = newCenter.lat();
        const lng = newCenter.lng();
        const lastLookup = lastReverseLookupRef.current;
        if (lastLookup) {
          const movedMeters = distanceInMeters(
            lastLookup.lat,
            lastLookup.lng,
            lat,
            lng,
          );
          const elapsedMs = Date.now() - lastLookup.at;
          if (
            movedMeters < MIN_DRAG_DISTANCE_METERS &&
            elapsedMs < MIN_REVERSE_LOOKUP_INTERVAL_MS
          ) {
            return;
          }
        }
        setCenter({ lat, lng });
        fetchPlaceDetails(lat, lng);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoadScript googleMapsApiKey={api_key || ""} libraries={libraries}>
      <div style={{ marginBottom: "10px", position: "relative" }}>
        <input
          type="text"
          placeholder="Search a place or drag map"
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          className="dm-map-search-input text-black placeholder-gray-500 dark:text-white dark:bg-gray-800 dark:border-gray-600"
          style={{
            width: "100%",
            padding: "10px 12px",
            fontSize: "14px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        />

        {showSuggestions && suggestions.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "44px",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              maxHeight: "220px",
              overflowY: "auto",
              zIndex: 20,
            }}
          >
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.placeId}
                type="button"
                onClick={() => handleSuggestionSelect(suggestion)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  border: "none",
                  borderBottom: "1px solid #f1f1f1",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                {suggestion.description}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ position: "relative" }}>
        <GoogleMap
          mapContainerStyle={{ height: "300px" }}
          center={center}
          zoom={13}
          onLoad={handleLoadMap}
          onDragEnd={handleDragEnd}
          options={{ draggable: true }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            fontSize: "32px",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          📍
        </div>
      </div>
    </LoadScript>
  );
};

export default MapWithSearch;
